import {
  ActionMenu,
  ConfirmationDialog,
  DataGrid,
  formatNumber,
  StatusBadge,
  useNotifications
} from '@ijoin/components';
import {
  getGetManagedAccountMethodologyAdvisoryOrganizationsQueryKey,
  getGetManagedAccountMethodologyProvidersQueryKey,
  GetManagedAccountMethodologyAdvisoryOrganizationRes,
  useDeleteManagedAccountMethodologyAdvisoryOrganization,
  useGetManagedAccountMethodologyAdvisoryOrganizations,
  usePostAdminAdvisoryOrganizationMethodology
} from '@ijoin/hooks';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';

import { camelCase } from 'lodash';
import { FC, useState } from 'react';

import { errorMessage } from '../helpers';
import { LinkButton } from './LinkButton';

type MethodologyAssociationsTabProps = {
  managedAccountMethodologyId: string;
};

export const MethodologyAssociationsTab: FC<
  MethodologyAssociationsTabProps
> = props => {
  const notifications = useNotifications();
  const queryClient = useQueryClient();

  const [pendingDelete, setPendingDelete] =
    useState<GetManagedAccountMethodologyAdvisoryOrganizationRes>();

  const associations = useGetManagedAccountMethodologyAdvisoryOrganizations(
    props.managedAccountMethodologyId
  );

  const assign = usePostAdminAdvisoryOrganizationMethodology({
    mutation: {
      onError: err =>
        notifications.error(
          errorMessage(err, 'Failed to assign methodology to organization.')
        ),
      onSuccess: () => {
        onAssociationsChanged();
        notifications.success('Methodology assigned to organization.');
      }
    }
  });
  const deleteAssociation =
    useDeleteManagedAccountMethodologyAdvisoryOrganization({
      mutation: {
        onError: err =>
          notifications.error(
            errorMessage(err, 'Failed to delete association.')
          ),
        onSuccess: () => {
          onAssociationsChanged();
          notifications.success('Association deleted.');
        }
      }
    });

  const onAssociationsChanged = () => {
    queryClient.invalidateQueries({
      queryKey: getGetManagedAccountMethodologyAdvisoryOrganizationsQueryKey(
        props.managedAccountMethodologyId
      )
    });
    queryClient.invalidateQueries({
      queryKey: getGetManagedAccountMethodologyProvidersQueryKey(
        props.managedAccountMethodologyId
      )
    });
  };

  const onCancelDelete = () => setPendingDelete(undefined);

  const onConfirmDelete = () => {
    if (!pendingDelete) return;

    deleteAssociation.mutate({
      advisoryOrganizationId: pendingDelete.advisoryOrganizationId,
      managedAccountMethodologyId: props.managedAccountMethodologyId
    });
    setPendingDelete(undefined);
  };

  const columns: GridColDef<GetManagedAccountMethodologyAdvisoryOrganizationRes>[] =
    [
      {
        field: 'name',
        flex: 2,
        headerName: 'Organization',
        renderCell: params => (
          <LinkButton
            data-testid={camelCase(params.row.name)}
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId
            }}
            to='/organizations/$advisoryOrganizationId'
            variant='text'>
            {params.value}
          </LinkButton>
        )
      },
      {
        align: 'right',
        field: 'providersCount',
        headerAlign: 'right',
        headerName: 'MAP Providers',
        valueFormatter: value => formatNumber(value),
        width: 130
      },
      {
        align: 'right',
        field: 'activePlansCount',
        headerAlign: 'right',
        headerName: 'Active Plans',
        valueFormatter: value => formatNumber(value),
        width: 120
      },
      {
        align: 'right',
        field: 'inactivePlansCount',
        headerAlign: 'right',
        headerName: 'Inactive Plans',
        valueFormatter: value => formatNumber(value),
        width: 130
      },
      {
        align: 'right',
        field: 'managedParticipantsCount',
        headerAlign: 'right',
        headerName: 'Managed Participants',
        valueFormatter: value => formatNumber(value),
        width: 180
      },
      {
        align: 'center',
        display: 'flex',
        field: 'isAssigned',
        headerAlign: 'center',
        headerName: 'Status',
        renderCell: params => (
          <StatusBadge
            data-testid={`status-${camelCase(params.row.name)}`}
            severity={params.value ? 'success' : 'info'}>
            {params.value ? 'Assigned' : 'Not Assigned'}
          </StatusBadge>
        ),
        width: 140
      },
      {
        align: 'center',
        display: 'flex',
        field: 'actions',
        headerAlign: 'center',
        headerName: '',
        renderCell: params => (
          <ActionMenu
            actions={
              params.row.isAssigned
                ? [
                    {
                      /* a disabled MenuItem swallows pointer events, so no Tooltip */
                      content: params.row.activePlansCount
                        ? 'Delete Association (in use by plans)'
                        : 'Delete Association',
                      destructive: true,
                      disabled: !!params.row.activePlansCount,
                      onSelect: () => setPendingDelete(params.row)
                    }
                  ]
                : [
                    {
                      content: 'Assign to Organization',
                      onSelect: () =>
                        assign.mutate({
                          advisoryOrganizationId:
                            params.row.advisoryOrganizationId,
                          managedAccountMethodologyId:
                            props.managedAccountMethodologyId
                        })
                    }
                  ]
            }
            trigger={
              <IconButton data-testid={`actions-${camelCase(params.row.name)}`}>
                <MoreVertIcon />
              </IconButton>
            }
          />
        ),
        sortable: false,
        width: 65
      }
    ];

  return (
    <>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={row => row.advisoryOrganizationId}
        initialSort={[{ field: 'name', sort: 'asc' }]}
        loading={associations.isFetching}
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        pinnedColumns={{ right: ['actions'] }}
        rows={associations.data ?? []}
        slotProps={{
          noRowsOverlay: { message: 'No advisory organizations' },
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'methodologyAssociations'
          }
        }}
        sx={{ flex: 1, minHeight: 0 }}
      />

      {pendingDelete && (
        <ConfirmationDialog
          confirmationText='delete'
          confirmColor='error'
          confirmText='Delete'
          data-testid='deleteMethodologyAssociation'
          isOpen
          message={`Remove this methodology from "${pendingDelete.name}". Advisors in this organization will no longer be able to select it for new providers, and its ${formatNumber(pendingDelete.providersCount)} managed account provider${pendingDelete.providersCount === 1 ? '' : 's'} will be hidden from them.`}
          onCancel={onCancelDelete}
          onConfirm={onConfirmDelete}
          title='Delete association'
        />
      )}
    </>
  );
};
